import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
    Box,
    Flex,
    Heading,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
} from '@chakra-ui/react';
import { FC, useState, useTransition } from 'react';
import { useLoaderData } from 'react-router-dom';
import ExcelIcon from '../../../images/icons/excel.svg';
import { fetchAppointments } from '../../api/repository/appointment';
import { fetchStock } from '../../api/repository/stock';
import { convertAppointmentFromApi } from '../../helpers/appointment';
import { convertJsonForExport } from '../../helpers/json';
import {
    convertStockItemFromApi,
    filterOnProperty,
    sortOnProperty,
} from '../../helpers/stock';
import { useExcel } from '../../hooks/useExcel';
import { AppointmentInterface } from '../../types/appointment';
import { StockItemInterface } from '../../types/stock';
import { StockOverviewTable } from './StockOverviewTable';

export const loader = async () => {
    const stock = await fetchStock();
    const appointments = await fetchAppointments();

    const formattedAppointments = appointments.map((item) =>
        convertAppointmentFromApi(item)
    );

    const formattedStock = stock?.map((item) => convertStockItemFromApi(item, formattedAppointments));

    return {
        stockCollection: formattedStock,
        appointments: formattedAppointments,
    };
};

export type ActiveStockOrderLabelType = {
    key: string;
    desc: boolean;
    isDate: boolean;
};

export const StockOverview: FC = () => {
    const { stockCollection, appointments } = useLoaderData() as {
        stockCollection: StockItemInterface[];
        appointments: AppointmentInterface[];
    };

    const [activeOrderLabel, setActiveOrderLabel] =
        useState<ActiveStockOrderLabelType>({
            key: 'apkDate',
            desc: false,
            isDate: true,
        });
    const [searchVal, setSearchVal] = useState('');
    const [filteredData, setFilteredData] =
        useState<StockItemInterface[]>(stockCollection);
    const [_isPending, startTransition] = useTransition();
    const { exportAsExcelFile } = useExcel();

    const sortOverview = (data: StockItemInterface[]) => {
        return data.sort(
            sortOnProperty(
                activeOrderLabel.key as keyof StockItemInterface,
                activeOrderLabel.desc,
                activeOrderLabel.isDate
            )
        );
    };

    const renderData = sortOverview(filteredData);

    const onExcelExport = (data: StockItemInterface[]) => {
        const json = convertJsonForExport(data);
        return exportAsExcelFile(json, 'Becx_mijn-voertuigen');
    };

    const onSearchChange = (val: string) => {
        setSearchVal(val);

        if (!val) {
            return startTransition(() => {
                setFilteredData(stockCollection);
            });
        }

        return startTransition(() => {
            const filtered = filterOnProperty(
                ['stockNumber', 'licensePlate', 'customerNote', 'appointmentState'],
                val,
                stockCollection
            );
            setFilteredData(filtered);
        });
    };

    const onClearSearch = () => {
        onSearchChange('');
    };

    const onOrderClick = (orderBy: ActiveStockOrderLabelType) => {
        setActiveOrderLabel(orderBy);
    };

    return (
        <Box px={8} py={8}>
            <Flex alignItems={'center'} justifyContent={'space-between'} mb={6}>
                <Heading m={0} color={'secondary'}>
                    Mijn voertuigen
                </Heading>

                <Flex alignItems={'center'} gap={4}>
                    <Box onClick={() => onExcelExport(stockCollection)}>
                        <IconButton
                            aria-label={''}
                            fontSize={'2xl'}
                            icon={<ExcelIcon />}
                            variant={'ghost'}
                        />
                    </Box>
                    <InputGroup>
                        <Input
                            placeholder="Zoeken..."
                            value={searchVal}
                            onChange={(e) =>
                                // startTransition(() => {
                                //     onSearchChange(e.target.value);
                                // })
                                onSearchChange(e.target.value)
                            }
                            borderRadius={'full'}
                        />
                        <InputRightElement py={2}>
                            {!!searchVal ? (
                                <IconButton
                                    aria-label={'Zoekveld legen'}
                                    onClick={onClearSearch}
                                    icon={<CloseIcon />}
                                    borderRadius={'full'}
                                    size={'sm'}
                                />
                            ) : (
                                <SearchIcon />
                            )}
                        </InputRightElement>
                    </InputGroup>
                </Flex>
            </Flex>

            <StockOverviewTable
                activeOrderLabel={activeOrderLabel}
                onOrderClick={onOrderClick}
                data={renderData}
                appointments={appointments}
            />
        </Box>
    );
};
